import { useId, type ComponentProps, type ReactNode } from 'react';
import { cn } from '../../lib/cn.ts';

const CONTROL =
  'w-full rounded-xl bg-surface px-4 text-[0.95rem] text-ink shadow-[inset_0_0_0_1px_var(--line)] outline-none focus-visible:outline-none transition-shadow placeholder:text-muted/70 hover:shadow-[inset_0_0_0_1px_color-mix(in_oklab,var(--ink)_22%,var(--line))] focus:shadow-[inset_0_0_0_2px_var(--brand)] aria-invalid:shadow-[inset_0_0_0_2px_var(--danger)]';

interface FieldFrameProps {
  label: string;
  hint?: ReactNode;
  error?: string;
  /** Characters used and allowed, shown while typing. */
  count?: { used: number; limit: number };
  className?: string;
  children: (ids: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode;
}

/** A label above, the control, then one line below for the error, the hint, or the count. */
export function FieldFrame({ label, hint, error, count, className, children }: FieldFrameProps) {
  const id = useId();
  const noteId = `${id}-note`;
  const hasNote = Boolean(error || hint);
  const over = count ? count.used > count.limit : false;
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label htmlFor={id} className="text-sm font-semibold text-ink">
        {label}
      </label>
      {children({ id, describedBy: hasNote ? noteId : undefined, invalid: Boolean(error) })}
      {hasNote || count ? (
        <div className="flex items-start justify-between gap-3 text-[0.8rem]">
          <p id={noteId} className={error ? 'font-medium text-danger' : 'text-muted'}>
            {error ?? hint}
          </p>
          {count ? (
            <span className={cn('shrink-0 tabular-nums', over ? 'font-semibold text-danger' : 'text-muted')}>
              {count.used}/{count.limit}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

type TextFieldProps = Omit<ComponentProps<'input'>, 'id'> & Omit<FieldFrameProps, 'children'>;

export function TextField({ label, hint, error, count, className, ...input }: TextFieldProps) {
  return (
    <FieldFrame label={label} hint={hint} error={error} count={count} className={className}>
      {({ id, describedBy, invalid }) => (
        <input id={id} aria-describedby={describedBy} aria-invalid={invalid || undefined} className={cn(CONTROL, 'h-11')} {...input} />
      )}
    </FieldFrame>
  );
}

type TextAreaFieldProps = Omit<ComponentProps<'textarea'>, 'id'> & Omit<FieldFrameProps, 'children'>;

export function TextAreaField({ label, hint, error, count, className, ...area }: TextAreaFieldProps) {
  return (
    <FieldFrame label={label} hint={hint} error={error} count={count} className={className}>
      {({ id, describedBy, invalid }) => (
        <textarea
          id={id}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          className={cn(CONTROL, 'min-h-28 resize-y py-3 leading-relaxed')}
          {...area}
        />
      )}
    </FieldFrame>
  );
}
